import { truncateText } from '@chronosphereio/core';
import { StateCreator } from 'zustand';
import { getUniquePanelKey, insertPanelInLayout, UnpositionedPanelGroupItemLayout } from '../../utils/panelUtils';
import { generateId, Middleware } from './common';
import { HistorySlice } from './history-slice';
import { PanelGroupItemId, PanelGroupSlice } from './panel-group-slice';
import { PanelSlice } from './panel-slice';

/**
 * Slice that handles duplicating Panels.
 */
export interface DuplicatePanelSlice {
  /**
   * Duplicate panel.
   */
  duplicatePanel: (panelGroupItemId: PanelGroupItemId) => void;
}

/**
 * Curried function for duplicating a panel.
 */
export function createDuplicatePanelSlice(): StateCreator<
  // Actions in here need to modify both Panels, Panel Groups, and History state
  DuplicatePanelSlice & PanelSlice & PanelGroupSlice & HistorySlice,
  Middleware,
  [],
  DuplicatePanelSlice
> {
  return (set, get) => ({
    duplicatePanel(panelGroupItemId: PanelGroupItemId) {
      get().initializeHistoryIfEmpty();

      // Used in undo notification
      let panelHistoryId = 'unnamed-panel';
      let panelHistoryName = 'Unnamed Panel';

      set(
        (state) => {
          const panels = state.panels;

          // Figure out the panel key at that location
          const { panelGroupId, panelGroupItemLayoutId: panelGroupLayoutId } = panelGroupItemId;
          const group = state.panelGroups[panelGroupId];
          if (group === undefined) {
            throw new Error(`Missing panel group ${panelGroupId}`);
          } else if (group.kind !== 'Grid' || !panelGroupLayoutId) {
            throw new Error(`Panel group ${panelGroupId} is not a Grid panel group`);
          }
          const panelKey = group.itemPanelKeys[panelGroupLayoutId];
          if (panelKey === undefined) {
            throw new Error(`Could not find panel group item ${panelGroupItemId}`);
          }

          // Find the panel to duplicate
          const panelToDupe = panels[panelKey];
          if (panelToDupe === undefined) {
            throw new Error(`Cannot find panel with key '${panelKey}'`);
          }

          // Find the layout for the item being duped
          const matchingLayout = group.itemLayouts.find((itemLayout) => {
            return itemLayout.i === panelGroupLayoutId;
          });

          if (matchingLayout === undefined) {
            throw new Error(`Cannot find layout for panel with key '${panelKey}'`);
          }

          const dupePanelKey = getUniquePanelKey({ panels });

          // Use panel name and panelKey in undo notification to ensure panelHistoryId is unique.
          // This is important so undo after duplicating same panel multiple times works.
          panelHistoryId = dupePanelKey;
          if (panelToDupe.spec.display.name) {
            panelHistoryName = panelToDupe.spec.display.name;
          }

          state.panels[dupePanelKey] = panelToDupe;

          const duplicateLayout: UnpositionedPanelGroupItemLayout = {
            i: generateId().toString(),
            w: matchingLayout.w,
            h: matchingLayout.h,
          };

          group.itemLayouts = insertPanelInLayout(duplicateLayout, matchingLayout, group.itemLayouts);

          group.itemPanelKeys[duplicateLayout.i] = dupePanelKey;
        },
        false,
        '[Panel] duplicatePanel'
      );

      get().pushToHistory({
        actionId: `duplicate-panel-${panelHistoryId}`,
        actionName: `Duplicated panel: ${truncateText(panelHistoryName, 20)}`,
      });
    },
  });
}
